import { CheckoutStepsType } from '@package/core/src/checkout/types';
import PayPalPaymentApiService from '@package/core/src/payment/PayPalPayment/PayPalPaymentApiService';
import PayPalPaymentInteractor from '@package/core/src/payment/PayPalPayment/PayPalPaymentInteractor';
import { CHEKOUT_SUMMARY_DATA_QUERY_KEY } from '@package/hooks/src/useCheckoutSummary/types';
import { useQueryClient } from '@package/hooks/src/useCustomQuery';
import { ApiError } from '@package/services/src/ApiClient';
import { PayPalButtons } from '@paypal/react-paypal-js';
import { useRouter } from 'next/router';
import config from 'config-base';
import { apiClient, storageManager } from '../../services';

import { PayPalButtonProps } from './types';

const paypalService = new PayPalPaymentApiService(apiClient);
const paypalInteractor = new PayPalPaymentInteractor(paypalService);

export default function PayPalButton(props: PayPalButtonProps) {
  const {
    hide = false,
    page,
    skipShippingVerification = false,
    label = 'pay',
    notDeliverable,
    onCallback,
    onError,
    handlePaypalApprove,
    height = 50,
  } = props;
  const queryClient = useQueryClient();
  const router = useRouter();

  if (hide) return null;

  return (
    <div data-testid={'paypal-btn'} style={{ height }}>
      <PayPalButtons
        style={
          {
            label,
            shape: 'rect',
            color: 'gold',
            layout: 'vertical',
            height,
            borderRadius: config.brandCode === '0' ? 1 : 3,
          } as any
        }
        forceReRender={[notDeliverable, skipShippingVerification, page, label]}
        createOrder={async (_, actions) => {
          const { data: checkoutObj } = await paypalInteractor.paypalCheckout({
            skipShippingVerification,
          });
          return actions.order.create(checkoutObj);
        }}
        onApprove={async (data) => {
          try {
            await paypalInteractor.paypalAuthorize({
              orderId: data.orderID,
              page,
              skipShippingVerification,
            });

            await queryClient.invalidateQueries([CHEKOUT_SUMMARY_DATA_QUERY_KEY]);

            if (handlePaypalApprove) handlePaypalApprove();
            if (onCallback) return await onCallback();

            await storageManager.put('disablePaymentStep', 'true');
            if (notDeliverable) {
              void router.push(CheckoutStepsType.Review);
            } else if (label === 'checkout' || label === 'paypal') {
              void router.push(CheckoutStepsType.ShippingAddress);
            } else {
              /* we're handling a corner case here, when a user changes
                shipping address on review step he should be redirected to
                shipping address page, but this callback fires faster than we get
                data from the backend, so we need to handle payment paypal redirection
                in PaymentStep component.
              */
              if (router.pathname !== CheckoutStepsType.Payments) {
                void router.push(CheckoutStepsType.Review);
              }
            }
          } catch (error) {
            if (error instanceof Error) {
              throw new Error(error.message);
            }
          }
        }}
        onError={(error) => {
          if (onError) onError(error as unknown as ApiError);
        }}
      />
    </div>
  );
}
